import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle } from 'lucide-react';
import { getContactEmail, EmailDisplay } from '../utils/emailObfuscator';
import '../styles/custom-scrollbar.css';


export const ContactFormModal = ({ isOpen, onClose, variant = 'pro' }) => {
  // Ajout du choix particuliers/professionnels
  const [type, setType] = useState(variant === 'particuliers' ? 'particuliers' : 'pro');
  const isParticuliers = type === 'particuliers';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    company: '',
    budget: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur quand l'utilisateur tape
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Veuillez entrer votre nom';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Veuillez entrer votre email';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    

    // Téléphone requis pour particuliers
    if (isParticuliers) {
      const phoneRegex = /^[0-9\s\-\+\(\)]{10,}$/;
      if (!formData.phone.trim()) {
        newErrors.phone = 'Veuillez entrer votre numéro de téléphone';
      } else if (!phoneRegex.test(formData.phone)) {
        newErrors.phone = 'Numéro de téléphone invalide';
      }
      // Service souhaité requis pour particuliers
      if (!formData.service) {
        newErrors.service = 'Veuillez sélectionner un service';
      }
    }
    
    // Société/Organisation requis uniquement pour pros
    if (!isParticuliers && !formData.company.trim()) {
      newErrors.company = 'Veuillez entrer votre société/organisation';
    }
    
    // Budget requis uniquement pour pros
    if (!isParticuliers && !formData.budget) {
      newErrors.budget = 'Veuillez sélectionner un budget estimé';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = isParticuliers 
        ? 'Veuillez décrire votre besoin'
        : 'Veuillez décrire votre projet';
    } else if (formData.message.length < 20) {
      newErrors.message = isParticuliers
        ? 'Décrivez un peu plus votre besoin (20 caractères minimum)'
        : 'Décrivez un peu plus votre projet (20 caractères minimum)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulation d'envoi (à remplacer par un vrai backend ou service comme Formspree)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Pour Formspree, décommenter et remplacer YOUR_FORM_ID
      const contactEmail = getContactEmail();
      // const response = await fetch('https://formspree.io/f/YOUR_FORM_ID', {
      //   method: 'POST',
      //   body: JSON.stringify({
      //     ...formData,
      //     _replyto: contactEmail
      //   }),
      //   headers: { 'Content-Type': 'application/json' }
      // });

      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', company: '', budget: '', message: '' });
      
      // Fermer le modal après 3 secondes
      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      setErrors({ submit: 'Erreur lors de l\'envoi. Veuillez réessayer.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop animé (fade + blur) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
            style={{ WebkitBackdropFilter: 'blur(8px)', backdropFilter: 'blur(8px)' }}
          />

          {/* Modal animé (fade + scale + y) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 40 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
              className="w-full max-w-2xl max-h-[90vh] min-h-0 bg-[#181C25] rounded-2xl border border-gray-700 shadow-2xl overflow-hidden flex flex-col relative"
              style={{ maxHeight: '90vh', minHeight: '0', display: 'flex', flexDirection: 'column', position: 'relative' }}
            >
              {/* Bouton fermeture FIXE, hors du scroll */}
              <button
                onClick={onClose}
                className="absolute top-6 right-6 p-2 hover:bg-gray-800 rounded-xl transition-all z-50"
                style={{ background: 'rgba(36, 41, 56, 0.7)', position: 'absolute', top: '1.5rem', right: '1.5rem', zIndex: 50 }}
              >
                <X size={24} className="text-gray-400 hover:text-white" />
              </button>
              {/* Header */}
              <div className="p-6 lg:p-8 border-b border-gray-700">
                <h2 className="text-2xl lg:text-3xl font-montserrat font-bold text-white">
                  {isParticuliers ? "Demander de l'aide" : 'Parler de votre projet'}
                </h2>
              </div>
              <div
                className="flex-1 min-h-0 overflow-y-auto p-4 rounded-xl custom-scrollbar"
                style={{
                  borderRadius: '0.75rem',
                  paddingRight: '0.75rem',
                  minHeight: 0,
                  maxHeight: 'calc(90vh - 6rem)', // pour header + padding
                }}
              >
                {submitSuccess ? (
                  // Message de succès
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="py-12 text-center"
                  >
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-montserrat font-bold text-white mb-2">
                      Formulaire envoyé !
                    </h3>
                    <p className="text-gray-400 mb-4">
                      Nous avons reçu votre demande et vous recontacterons dans les 24-48 heures.
                    </p>
                    <p className="text-gray-500 text-sm">
                      Merci de votre intérêt pour nos services !
                    </p>
                  </motion.div>
                ) : (
                  // Formulaire
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Sélecteur particuliers/professionnels */}
                    <div className="flex gap-4 mb-2">
                      <button
                        type="button"
                        className={`px-4 py-2 rounded-lg font-semibold border transition-all ${isParticuliers ? 'bg-orange-500 text-white border-orange-600' : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-orange-500/10 hover:text-orange-500'}`}
                        onClick={() => setType('particuliers')}
                        aria-pressed={isParticuliers}
                      >
                        Particuliers
                      </button>
                      <button
                        type="button"
                        className={`px-4 py-2 rounded-lg font-semibold border transition-all ${!isParticuliers ? 'bg-blue-600 text-white border-blue-700' : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-blue-600/10 hover:text-blue-500'}`}
                        onClick={() => setType('pro')}
                        aria-pressed={!isParticuliers}
                      >
                        Professionnels
                      </button>
                    </div>
                    {/* Service souhaité pour particuliers */}
                    {isParticuliers && (
                      <div>
                        <label className="block text-white font-semibold mb-2">
                          Pour quel service souhaitez-vous des informations ? *
                        </label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors"
                        >
                          <option value="">Sélectionnez un service</option>
                           <option value="ordi">Installer ou configurer un ordinateur (Apple ou Windows)</option>
                           <option value="depannage">Dépanner ou booster un PC</option>
                           <option value="assistance-numerique">Assistance numérique (messagerie, démarches, sécurité...)</option>
                           <option value="fibre">Assistance fibre et configuration réseau</option>
                           <option value="autre">Autre / Je ne sais pas</option>
                        </select>
                        {errors.service && (
                          <p className="text-red-500 text-sm mt-1">{errors.service}</p>
                        )}
                      </div>
                    )}
                    {/* Nom */}
                    <div>
                      <label className="block text-white font-semibold mb-2">
                        Votre nom *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="Votre nom"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-white font-semibold mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="votre@email.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>

                    {/* Téléphone - requis pour particuliers */}
                    {isParticuliers && (
                      <div>
                        <label className="block text-white font-semibold mb-2">
                          Numéro de téléphone *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors"
                          placeholder="06 12 34 56 78"
                        />
                        {errors.phone && (
                          <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                        )}
                      </div>
                    )}

                    {/* Compagnie - uniquement pour pros */}
                    {!isParticuliers && (
                      <div>
                        <label className="block text-white font-semibold mb-2">
                          Société / Organisation *
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors"
                          placeholder="Votre entreprise"
                        />
                        {errors.company && (
                          <p className="text-red-500 text-sm mt-1">{errors.company}</p>
                        )}
                      </div>
                    )}

                    {/* Budget - uniquement pour pros */}
                    {!isParticuliers && (
                      <div>
                        <label className="block text-white font-semibold mb-2">
                          Budget estimé *
                        </label>
                        <select
                          name="budget"
                          value={formData.budget}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors"
                        >
                          <option value="">Sélectionnez un budget</option>
                          <option value="moins-5k">&lt; 5 000 €</option>
                          <option value="5k-15k">5 000 - 15 000 €</option>
                          <option value="15k-50k">15 000 - 50 000 €</option>
                          <option value="plus-50k">&gt; 50 000 €</option>
                          <option value="non-defini">Non défini</option>
                        </select>
                        {errors.budget && (
                          <p className="text-red-500 text-sm mt-1">{errors.budget}</p>
                        )}
                      </div>
                    )}

                    {/* Message */}
                    <div>
                      <label className="block text-white font-semibold mb-2">
                        {isParticuliers ? 'En quoi puis-je vous aider ? *' : 'Décrivez votre projet *'}
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="5"
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none transition-colors resize-none"
                        placeholder={isParticuliers 
                          ? "Décrivez votre besoin, votre problème ou la question que vous vous posez..."
                          : "Parlez-nous de votre projet, vos défis, vos objectifs..."}
                      />
                      {errors.message && (
                        <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                      )}
                    </div>

                    {errors.submit && (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                        {errors.submit}
                      </div>
                    )}

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold hover:shadow-lg hover:shadow-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                    </motion.button>

                    <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                      <p className="text-gray-300 text-sm mb-2">
                        Vous préférez nous contacter directement ?
                      </p>
                      <EmailDisplay className="text-blue-400 hover:text-blue-300 font-semibold text-sm transition-colors" />
                    </div>

                    <p className="text-gray-500 text-xs text-center">
                      Nous vous répondrons sous 24-48 heures.
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactFormModal;
